/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';
import {HttpClient, HttpEvent, HttpRequest} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {BuildToolHarness} from '../model/buildToolHarness.model';


@Injectable({
    providedIn: 'root'
})
export class FileUploadService {
    buildCheckToolUrl = '/esciUI/buildCheckTool';
    processHarnessesUrl = '/esciUI/processHarnesses';
    generateKitMatrixUrl = '/esciUI/generateKitMatrix';
    generateNetlistCompareUrl = '/esciUI/generateNetlistCompare';
    generateBctDetailsUrl = '/esciUI/generateBctDetails';
    generateIdenticalConnectorUrl = '/esciUI/generateIdenticalConnector';
    vehicleBuildCompareUrl = '/esciUI/vehicleBuildCompare';
    extractionCompareUrl = '/esciUI/extractionCompare';
    generateCrm2Url = '/esciUI/generateCrm2';

    private blobOptions = {
        reportProgress: true,
        responseType: 'blob'
    };

    private jsonOptions = {
        reportProgress: true,
        responseType: 'json'
    };

    constructor(private http: HttpClient) {
    }

    uploadFilesForReport(harnessMatrixFile: Blob, vehicleBuildFile: File, kitMatrixFile: File, bypassValidation: boolean): Observable<HttpEvent<any>> {
        const formData: FormData = new FormData();
        formData.append('harnessMatrixFile', harnessMatrixFile);
        formData.append('vehicleBuildFile', vehicleBuildFile);
        formData.append('kitMatrixFile', kitMatrixFile);
        formData.append('bypassValidation', String(bypassValidation));
        return this.sendHttpRequest(formData, this.buildCheckToolUrl, this.jsonOptions);
    }

    uploadFilesForHarnesses(harnessMatrixFile: Blob, bypassValidation: boolean): Observable<HttpEvent<any>> {
        const formData: FormData = new FormData();
        formData.append('harnessMatrixFile', harnessMatrixFile);
        formData.append('bypassValidation', String(bypassValidation));
        return this.sendHttpRequest(formData, this.processHarnessesUrl, this.jsonOptions);
    }

    uploadFilesForKitMatrix(vaaKits: Array<BuildToolHarness>, harnessKits: Array<BuildToolHarness>,
                            poaKits: Array<BuildToolHarness>, harnessMatrixFile: Blob, vehicleBuildFile: File,
                            bypassValidation: boolean): Observable<HttpEvent<any>> {
        const formData: FormData = new FormData();
        const vaaKitList = new Array<string>;
        vaaKits.forEach(function (o) {
            vaaKitList.push(o.harnessName);
        });
        const harnessKitList = new Array<string>;
        harnessKits.forEach(function (o) {
            harnessKitList.push(o.harnessName);
        });
        const poaKitList = new Array<string>;
        poaKits.forEach(function (o) {
            poaKitList.push(o.harnessName);
        });
        formData.append('vaaKitList', JSON.stringify(vaaKitList));
        formData.append('harnessKitList', JSON.stringify(harnessKitList));
        formData.append('poaKitList', JSON.stringify(poaKitList));
        formData.append('harnessMatrixFile', harnessMatrixFile);
        formData.append('vehicleBuildFile', vehicleBuildFile);
        formData.append('bypassValidation', String(bypassValidation));
        return this.sendHttpRequest(formData, this.generateKitMatrixUrl, this.blobOptions);
    }

    uploadFilesForBctDetails(harnessMatrixFile: File, vehicleBuildFile: File,
                             bypassValidation: boolean): Observable<HttpEvent<any>> {
        const formData: FormData = new FormData();
        formData.append('harnessMatrixFile', harnessMatrixFile);
        formData.append('vehicleBuildFile', vehicleBuildFile);
        formData.append('bypassValidation', String(bypassValidation));
        return this.sendHttpRequest(formData, this.generateBctDetailsUrl, this.blobOptions);
    }

    uploadFilesForNetlistCompare(hrnFile1: Blob, hrnFile2: Blob, prolFile: File | null, plocFile: File | null,
                                 harnessName: string | null): Observable<HttpEvent<any>> {
        const formData: FormData = new FormData();
        formData.append('hrnFile1', hrnFile1);
        formData.append('hrnFile2', hrnFile2);
        if (prolFile) {
            formData.append('prolFile', prolFile);
        }
        if (plocFile) {
            formData.append('plocFile', plocFile);
        }
        formData.append('harnessName', harnessName ? harnessName : '');
        return this.sendHttpRequest(formData, this.generateNetlistCompareUrl, this.jsonOptions);
    }

    uploadFilesForIdenticalConnector(harnessZip: Blob): Observable<HttpEvent<any>> {
        const formData: FormData = new FormData();
        formData.append('zipFile', harnessZip);
        return this.sendHttpRequest(formData, this.generateIdenticalConnectorUrl, this.jsonOptions);
    }

    uploadFilesForVehicleBuildCompare(vehicleBuild1: File, vehicleBuild2: File): Observable<HttpEvent<any>> {
        const formData: FormData = new FormData();
        formData.append('vehicleBuild1', vehicleBuild1);
        formData.append('vehicleBuild2', vehicleBuild2);
        return this.sendHttpRequest(formData, this.vehicleBuildCompareUrl, this.jsonOptions);
    }

    uploadFilesForExtractionCompare(file1: File, file2: File, prolFile: File | null): Observable<HttpEvent<any>> {
        const formData: FormData = new FormData();
        formData.append('file1', file1);
        formData.append('file2', file2);
        if (prolFile) {
            formData.append('prolFile', prolFile);
        }
        return this.sendHttpRequest(formData, this.extractionCompareUrl, this.blobOptions);
    }

    uploadFilesForCrm2(hrnFileSetZip: Blob): Observable<HttpEvent<any>> {
        const formData: FormData = new FormData();
        formData.append('hrnFileSetZip', hrnFileSetZip);
        return this.sendHttpRequest(formData, this.generateCrm2Url, this.jsonOptions);
    }

    private sendHttpRequest(formData: FormData, url: string, options: {}) {
        const req = new HttpRequest('POST', `${environment.apiUrl}${url}`, formData, options);
        return this.http.request(req);
    }

}

